<script setup lang="ts">
import { ref, computed } from 'vue';
import { useMainStore } from '../../store';
import { useLeadSubmit } from '../../composables/useLeadSubmit';
import { useGateAnalytics } from '../../composables/useGateAnalytics';
import { trackQuizQuestion } from '../../analytics/events';
import type { FlowScreen, Gate, QuizData } from '../../types';
import type { TemplateDefinition } from '../../templates';

// Combined single-page block (DEV-1135, slider-funnel mechanic): ONE question
// answered inline (slider/select) on the same screen as the lead form. Same
// money path as GateBlock (useLeadSubmit); the inline answer is recorded just
// before submit so buildLead sees it. The template's questionGate component is
// presentation only: it reads `ui.answerIndex` (mutable model, like the gate's
// consent object) and emits submit.
const props = defineProps<{
  screen: Extract<FlowScreen, { type: 'question-gate' }>;
  template: TemplateDefinition;
}>();

const store = useMainStore();
const quizData = store.quizData as QuizData;
const gate = (store.gateData || {}) as Gate;
const form = ref(store.formLead);
const consentState = ref({ checked: false });
// Presentational answer state for the template (e.g. slider position).
const ui = ref({ answerIndex: 0 });

const question = computed(() =>
  store.quizData.questions?.find((q) => q.id === props.screen.question_id)
);

useGateAnalytics(quizData, form);

const hasConsent = !!gate.consent_checkbox;
const { submit, skip } = useLeadSubmit(
  quizData,
  form,
  hasConsent ? { consent: computed(() => consentState.value.checked) } : undefined
);

const recordInlineAnswer = () => {
  const q = question.value;
  if (!q || !q.answers?.length) return;
  const idx = Math.min(Math.max(ui.value.answerIndex, 0), q.answers.length - 1);
  const answer = q.answers[idx];
  const already = store.questionAnswers.some(
    ([qId, a]) => qId === q.id && a === answer[0]
  );
  if (!already) {
    trackQuizQuestion({
      quiz_id: store.quizData.quiz_id,
      question_index: 0,
      question_text: q.title,
      answer_text: String(answer[1]),
    });
  }
  store.recordFlowAnswer([q.id, answer[0]]);
};

const handleSubmit = async () => {
  recordInlineAnswer();
  await submit();
};

const handleSkip = () => {
  recordInlineAnswer();
  skip();
};
</script>

<template>
  <component
    :is="template.questionGate"
    v-if="question"
    :question="question"
    :gate="gate"
    :quiz="quizData"
    :form="form"
    :ui="ui"
    :consent="consentState"
    :fetching="store.fetching"
    @submit="handleSubmit"
    @skip="handleSkip"
  />
</template>
